import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { getDataFromResponse } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button/Button";
import DatePickerInput from "~/components/formElements/DatePickerInput";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import Input from "~/components/formElements/Input";
import SubmitButton from "~/components/formElements/SubmitButton";
import Textarea from "~/components/formElements/Textarea";
import Headline from "~/components/Headline/Headline";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { createReversalInvoice } from "~/modules/billing/api/processedInvoice/processedInvoiceApiDispatchers.ts";
import type { Invoice } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import InvoiceTotalsFormSection from "~/modules/billing/components/InvoiceTotalsFormSection/InvoiceTotalsFormSection";
import { DEFAULT_VAT_PERCENTAGE } from "~/modules/billing/constants/billingConstants.ts";
import { formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type FormData = {
	comment: string | null;
	invoiceSentAt: Date;
	isGrossBased: boolean;
	recipientInformation: string | null;
	subtotalCents: number;
	totalAmountCents: number;
	travelCostCentsBeforeVat: number;
	vatPercentage: number;
}

type Props = {
	onCancel: () => void;
	onSuccess: (newInvoiceId: string) => void;
	originalInvoiceData: Invoice;
};

const today = new Date();

const CreateReversalInvoiceForm = ({
	onCancel,
	onSuccess,
	originalInvoiceData,
}: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");
	const isNonProjectInvoice = !originalInvoiceData.isProjectInvoice;
	const schema = useMemo(() => {
		return yup.object({
			comment: yup.string().nullable().default(""),
			invoiceSentAt: yup.date().required(),
			isGrossBased: yup.boolean().required().default(false),
			recipientInformation: yup.string().nullable().default(""),
			subtotalCents: yup.number().default(0).when("isGrossBased", {
				is: false,
				then: (schema) => schema.required().max(-1, "Der Nettobetrag darf nicht größer als 0 sein."),
				otherwise: (schema) => schema.optional(),
			}),
			totalAmountCents: yup.number().default(0).when("isGrossBased", {
				is: true,
				then: (schema) => schema.required().max(-1, "Der Bruttobetrag darf nicht größer als 0 sein."),
				otherwise: (schema) => schema.optional(),
			}),
			travelCostCentsBeforeVat: yup.number().required(),
			vatPercentage: yup.number().required(),
		});
	}, []);

	const defaultValues = useMemo(() => {
		return {
			comment: "",
			invoiceSentAt: new Date(),
			isGrossBased: !isNonProjectInvoice ? false : originalInvoiceData.isGrossBased,
			recipientInformation: "",
			subtotalCents: originalInvoiceData.subtotalCents * -1,
			totalAmountCents: originalInvoiceData.totalAmountCents * -1,
			travelCostCentsBeforeVat: originalInvoiceData.travelCostCentsBeforeVat * -1,
			vatPercentage: DEFAULT_VAT_PERCENTAGE,
		};
	}, [isNonProjectInvoice, originalInvoiceData.isGrossBased, originalInvoiceData.subtotalCents, originalInvoiceData.totalAmountCents, originalInvoiceData.travelCostCentsBeforeVat]);

	const {
		handleSubmit,
		control,
		formState: { isSubmitted, isValid, errors },
		setValue,
		watch,
	} = useForm<FormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<FormData>(schema),
		mode: "onChange",
	});

	const onSubmit = useCallback(async (data: FormData) => {
		try {
			setIsBusy(true);
			const updateData = {
				...data,
				invoiceSentAt: formatDateToYYYYMMDD(data.invoiceSentAt),
				originalInvoiceId: originalInvoiceData.id,
			};

			const res = await createReversalInvoice(updateData);
			const reversalInvoiceId = getDataFromResponse(res).reversalInvoiceId;
			onSuccess(reversalInvoiceId);
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
			setIsBusy(false);
		}
	}, [originalInvoiceData.id, onSuccess]);

	const formIsSubmittable = isValid && !isBusy;


	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Storno-Rechnung erzeugen</SidebarHeaderHeadline>
			</SidebarHeader>
			<div className="grow gap-x-8 overflow-auto px-7 py-2">
				{isBusy && <SidebarBusyOverlay />}
				{serverErrorMessage &&
					<SidebarErrorOverlay title="Update fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
				<div className="grid h-auto grid-cols-2 gap-4 pt-1">
					<div className="col-span-2">
						<Headline className="mb-2"
								  type="h4">Empfänger</Headline>
						<Input control={control}
							   label="Versendet an"
							   name="recipientInformation"
							   placeholder="Name, Versandart, ggf. E-Mail-Adresse"
						/>
					</div>
					<DatePickerInput name="invoiceSentAt"
									 label="Versendet am / Rechnungsdatum"
									 control={control}
									 minDate={today} />
					<InvoiceTotalsFormSection<FormData> control={control}
														setValue={setValue}
														errorMessage={errors?.totalAmountCents?.message || errors?.subtotalCents?.message}
														allowGrossInput={isNonProjectInvoice}
														includeTravelCosts={!isNonProjectInvoice}
														watch={watch}
														title="Rechnungsbetrag" />
					<div className="col-span-2 mt-4">
						<Headline className="mb-2"
								  type="h4">Kommentar</Headline>
						<Textarea control={control}
								  name="comment"
								  placeholder="Hier können Besonderheiten zum Abrechnungsprozess vermerkt werden."
						/>
					</div>
					{!isNonProjectInvoice && <div className="col-span-2 mt-4">
						<Headline className="mb-2"
								  type="h4">Projekterfassungen</Headline>
						<p className="leading-tight text-gray-800">Die in der Originalrechnung abgerechneten Erfassungen werden durch den Stornovorgang wieder freigegeben, so dass sie erneut abgerechnet werden können / müssen.</p>
					</div>}
				</div>
			</div>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={isBusy}
							  disabled={!formIsSubmittable}>
					Storno-Rechnung erzeugen
				</SubmitButton>
				<Button theme="white"
						onClick={onCancel}>
					Abbrechen
				</Button>
			</SidebarFooter>
		</form>);
};

export default CreateReversalInvoiceForm;
